import { CommonModule } from "@angular/common";
import {
  Component,
  ViewEncapsulation,
  ViewChild,
  inject,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatOptionModule, MatRippleModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSort, MatSortModule, Sort } from "@angular/material/sort";
import { UserFormComponent } from "./user-form/user-form.component";
import { MatDialog } from "@angular/material/dialog";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatDialogModule } from "@angular/material/dialog";
import { UserDetailComponent } from "./user-detail/user-detail.component";
import { MatChipsModule } from "@angular/material/chips";
import { BreadcrumbComponent } from "app/layout/common/breadcrumb/breadcrumb.component";
import { ApiService } from "app/service/api.service";
import { API_ROUTE } from "app/api-routes";
import { FuseConfirmationDialogComponent } from "@fuse/services/confirmation/dialog/dialog.component";
import { HttpParams } from "@angular/common/http";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";

import {
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  Subject,
  switchMap,
} from "rxjs";
import { set } from "lodash";
import { MatSnackBar } from "@angular/material/snack-bar";
import { BaseComponent } from "app/layout/base/base.component";
import { MatTooltip } from "@angular/material/tooltip";
import { TOAST_DURATION } from "app/app-constant";

export interface User {
  username: string;
  userType: "admin" | "user";
  password: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  address: string;
  lastLogin?: Date;
  failedLoginAttempts?: number;
  lockoutUntil?: Date;
  lastFailedLogin?: Date;
  passwordHistory?: string;
  isActive: boolean;
  metadata?: string;
  roleId?: string;
}

@Component({
  selector: "user-list",
  standalone: true,
  templateUrl: "./user.component.html",
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    CommonModule,
    MatChipsModule,
    BreadcrumbComponent,
    FormsModule,
    ReactiveFormsModule,
    MatSortModule,
    MatSelectModule,
    MatTooltip,
  ],
})
export class UserComponent extends BaseComponent {
  private _snackBar = inject(MatSnackBar);
  displayedColumns: string[] = [
    "serial",
    "username",
    "firstName",
    "lastName",
    "email",
    "role",
    "status",
    "actions",
  ];
  dataSource: MatTableDataSource<User> = new MatTableDataSource<User>([]);
  private searchSubject = new Subject<{ search: string; status: string }>();

  searchForm: FormGroup;
  pagination = {
    length: 0,
    limit: 10,
    page: 0,
  };
  users: User[] = [];
  @ViewChild(MatSort) sort!: MatSort;
  isLoading: boolean = false;
  skip: number = 1;
  limit: number = 10;
  roleList: any[] = [];
  userStatusList: string[] = ["new", "active", "archived"];

  selectedStatus: string = "";

  // Maps a mat-sort-header id (the .html column id) to the backend field to
  // sort by. firstName/lastName/email live under the nested `profile` path
  // on the User schema, so the html ids don't match the backend field names.
  private readonly sortFieldMap: Record<string, string> = {
    username: "username",
    firstName: "profile.firstName",
    lastName: "profile.lastName",
    email: "profile.email",
  };
  activeSortField: string | null = null;
  activeSortOrder: "asc" | "desc" = "desc";

  constructor(
    public dialog: MatDialog,
    private _apiSerivce: ApiService,
    private fb: FormBuilder,
  ) {
    super();
    // this.dataSource = new MatTableDataSource(this.users);
    this.initData();
    this.searchForm = this.fb.group({
      search: [""], // Input field for search
    });

    this.searchSubject
      .pipe(
        debounceTime(300), // Wait for the user to stop typing
        distinctUntilChanged(), // Avoid duplicate queries
        switchMap(({ search, status }) => {
          // Stop the previous request and start a new one
          this.isLoading = true;
          let params = new HttpParams()
            .set("page", String(this.pagination.page + 1))
            .set("limit", String(this.pagination.limit));

          if (search && search.trim()) {
            params = params.set("username", search.trim());
          }

          if (status) {
            params = params.set("status", status);
          }

          if (this.activeSortField) {
            params = params
              .set("sortField", this.activeSortField)
              .set("sortOrder", this.activeSortOrder);
          }

          return this._apiSerivce.get(API_ROUTE.USER.getAll, params);
        }),
      )

      .subscribe({
        next: (res: any) => {
          this.users = res.data.users;
          this.pagination.length = res.data.totalItems;
          this.pagination.limit = res.data.limit;
          this.pagination.page = res.data.currentPage - 1;

          this.setupTableData(res.data.users);
          this.isLoading = false;
        },
        error: (err) => {
          this.openSnackBar(err.error.msg);
        },
      });
  }

  setupTableData(data: any[]) {
    const tableData = data.map((e) => {
      return {
        ...e,
        firstName: e.profile?.firstName,
        lastName: e.profile?.lastName,
        email: e.profile?.email,
        role: this.getRoleName(e.roleId),
      };
    });
    this.dataSource = new MatTableDataSource(tableData);
  }

  onSortChange(sort: Sort): void {
    if (!sort.direction) {
      this.activeSortField = null;
    } else {
      this.activeSortField = this.sortFieldMap[sort.active] ?? sort.active;
      this.activeSortOrder = sort.direction;
    }
    this.pagination.page = 0;
    this.initData();
  }

  initData() {
    this.isLoading = true;
    let params = new HttpParams()
      .set("page", this.pagination.page + 1)
      .set("limit", this.pagination.limit);
    // Carry over the active search/status filter — initData() is also used
    // for pagination and sort changes, which shouldn't silently drop them.
    // Optional chaining on searchForm: initData() is also called once from
    // the constructor before searchForm is initialized.
    const search = this.searchForm?.get("search")?.value?.trim();
    if (search) {
      params = params.set("username", search);
    }
    if (this.selectedStatus) {
      params = params.set("status", this.selectedStatus);
    }
    if (this.activeSortField) {
      params = params
        .set("sortField", this.activeSortField)
        .set("sortOrder", this.activeSortOrder);
    }
    const roleParams = new HttpParams().set("page", 1).set("limit", 100);
    const obs = {
      usersResponse: this._apiSerivce.get(API_ROUTE.USER.getAll, params),
    };
    if (!this.roleList.length)
      obs["rolesResponse"] = this._apiSerivce.get(
        API_ROUTE.ROLE.getAll,
        roleParams,
      );
    forkJoin(obs).subscribe(
      (result: any) => {
        // Handle roles response
        if (result.rolesResponse) {
          const rolesRes = result.rolesResponse;
          this.roleList = rolesRes.data.roles;
        }
        // Handle users response
        const usersRes = result.usersResponse;
        this.pagination.length = usersRes.data.totalItems;
        this.pagination.limit = usersRes.data.limit;
        this.pagination.page = usersRes.data.currentPage - 1;
        this.users = usersRes.data.users;
        this.setupTableData(usersRes.data.users);
      },
      (error) => {},
    );
  }
  // onSearch(event: KeyboardEvent): void {
  // if (event.key === "Enter") {
  // const query = this.searchForm.get("search")?.value.trim() || "";
  // this.searchSubject.next(query,);

  // }
  // }
  onSearch(event: KeyboardEvent): void {
    // if (event.key === "Enter") {
    const search = this.searchForm.get("search")?.value?.trim() || "";

    this.searchSubject.next({
      search: search,
      status: this.selectedStatus || "",
    });
    // }
  }

  createUser(): void {
    if (!this.hasDetailedPerm("User_Management", "Create", "read")) return;

    const dialogRef = this.dialog.open(UserFormComponent, {
      width: "60%", // Adjust the width if necessary
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.initData();
    });
  }

  viewDetail(user: any): void {
    const dialogRef = this.dialog.open(UserDetailComponent, {
      width: "50%", // Adjust the width if necessary
      data: user,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.action === "edit") {
        this.editRole(result.data);
      }
    });
  }

  // Method to handle pagination changes
  onPaginateChange(event: any): void {
    this.pagination.page = event.pageIndex;
    this.pagination.limit = event.pageSize;
    this.initData();
  }

  editRole(user: any) {
    if (!this.hasDetailedPerm("User_Management", "Edit", "read")) return;

    const dialogRef = this.dialog.open(UserFormComponent, {
      width: "50%",
      data: user,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.initData();
    });
  }

  getRoleName(id: string) {
    return this.roleList.find((e) => e._id === id)?.name;
  }

  getBadgeClass(status: string): string {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "archived":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  }
  openSnackBar(message: string, action: string = "") {
    this._snackBar.open(message, action, {
      horizontalPosition: "end",
      verticalPosition: "top",
      duration: TOAST_DURATION,
      panelClass: "app-snackBar",
    });
  }
  get searchVal() {
    return this.searchForm.get("search")?.value;
  }
  onStatusChange(status: string): void {
    this.pagination.page = 0;
    this.searchSubject.next({
      search: this.searchVal,
      status,
    });
  }

  deleteUser(user: any, archive: boolean = true) {
    if (!this.hasDetailedPerm("User_Management", "Delete", "read")) return;

    const dialogRef = this.dialog.open(FuseConfirmationDialogComponent, {
      data: {
        title: `${archive ? "Archive" : "Unarchive"} Confirmation`,
        message: `Are you sure you want to ${archive ? "archive" : "Unarchive"} user  <b>${user.username}</b> ?`,
        icon: "heroicons_outline:exclamation-triangle",
        actions: {
          confirm: {
            show: true,
            label: "Yes",
            color: "warn",
          },
          cancel: {
            show: true,
            label: "No",
          },
        },
        dismissible: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === "confirmed") {
        this._apiSerivce
          .put(API_ROUTE.USER.delete(archive ? "delete" : "unarchive"), {
            id: user._id,
          })
          .subscribe({
            next: (res: any) => {
              this.openSnackBar(res.msg);
              this.searchSubject.next({
                search: this.searchVal,
                status: this.selectedStatus,
              });
            },
            error: (err: any) => {
              this.openSnackBar(
                err?.error?.msg || err?.error?.message || "Failed to update user.",
              );
            },
          });
      } else {
      }
    });
  }
}
